'use client'

import { cn } from '@/lib/tailwind'
import { MagnifyingGlassIcon } from '@phosphor-icons/react'
import { Input } from '@/ui/input'
import { FilterChips } from '@/ui/filter-chips'
import { TopicFilter } from '@/topics'
import { ViewSwitcher, type ViewMode } from '@/ui/view-switcher'
import { FILTER_OPTIONS } from './filter-options'
import type { FilterType } from './types'
import { useRef, useEffect, useState } from 'react'

interface FeedHeaderProps {
  filter: FilterType
  onFilterChange: (value: FilterType) => void
  // Search and topic filter props
  searchQuery?: string
  onSearchChange?: (query: string) => void
  selectedTopics?: string[]
  onTopicsChange?: (topics: string[]) => void
  activeFilters?: Array<{ id: string; label: string; onRemove: () => void }>
  onClearAllFilters?: () => void
  // Show completed tasks toggle
  showCompleted?: boolean
  onToggleCompleted?: (show: boolean) => void
  // Result count
  resultCount?: number
  totalCount?: number
  // View mode switcher
  viewMode?: ViewMode
  onViewModeChange?: (mode: ViewMode) => void
  availableViewModes?: ViewMode[]
}

/**
 * Feed header with filter dropdown, search, and topic filter
 */
export function FeedHeader({
  filter,
  onFilterChange,
  searchQuery,
  onSearchChange,
  selectedTopics,
  onTopicsChange,
  activeFilters,
  onClearAllFilters,
  // showCompleted, onToggleCompleted, resultCount, totalCount — reserved for future use
  viewMode,
  onViewModeChange,
  availableViewModes,
}: FeedHeaderProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const activeTabRef = useRef<HTMLButtonElement>(null)
  const [clipPath, setClipPath] = useState<string>(
    'inset(0 100% 0 0 round 17px)',
  )

  useEffect(() => {
    const container = containerRef.current
    const activeTab = activeTabRef.current
    if (!container || !activeTab) return

    const containerRect = container.getBoundingClientRect()
    const tabRect = activeTab.getBoundingClientRect()

    const leftPx = tabRect.left - containerRect.left
    const rightPx = containerRect.right - tabRect.right

    const containerWidth = containerRect.width
    const leftPct = (leftPx / containerWidth) * 100
    const rightPct = (rightPx / containerWidth) * 100

    setClipPath(
      `inset(0 ${rightPct.toFixed(2)}% 0 ${leftPct.toFixed(2)}% round 17px)`,
    )
  }, [filter])

  return (
    <div className="space-y-3">
      {/* Header row with inline category switcher and view mode switcher */}
      <div className="flex items-center justify-between">
        {/* Animated inline text category switcher */}
        <div className="relative flex items-baseline gap-1" ref={containerRef}>
          {/* Background layer — muted text */}
          <div className="flex items-baseline gap-1">
            {FILTER_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onFilterChange(option.value)}
                className={cn(
                  'text-sm font-medium',
                  'min-h-[44px] px-1.5 sm:min-h-0',
                  'text-muted-foreground/40 hover:text-muted-foreground/70',
                  'transition-colors',
                )}
                tabIndex={-1}
                aria-hidden="true"
              >
                {option.value === 'all' ? 'Recent' : option.label}
              </button>
            ))}
          </div>

          {/* Overlay layer — foreground text, clipped to active tab */}
          <div
            className={cn(
              'pointer-events-none absolute inset-0 flex items-baseline gap-1',
              'transition-[clip-path] duration-[250ms] ease-in-out',
              'motion-reduce:transition-none',
            )}
            style={{ clipPath }}
            aria-hidden="true"
          >
            <div className="bg-foreground/10 absolute inset-0 rounded-[17px]" />
            {FILTER_OPTIONS.map((option) => (
              <span
                key={option.value}
                className={cn(
                  'text-sm font-medium',
                  'min-h-[44px] px-1.5 sm:min-h-0',
                  'text-foreground',
                  'inline-flex items-center',
                )}
              >
                {option.value === 'all' ? 'Recent' : option.label}
              </span>
            ))}
          </div>

          {/* Real interactive buttons (transparent text, on top for interaction) */}
          <div className="absolute inset-0 flex items-baseline gap-1">
            {FILTER_OPTIONS.map((option) => (
              <button
                key={option.value}
                ref={filter === option.value ? activeTabRef : null}
                type="button"
                onClick={() => onFilterChange(option.value)}
                className={cn(
                  'text-sm font-medium',
                  'min-h-[44px] px-1.5 sm:min-h-0',
                  'text-transparent',
                  'select-none',
                )}
              >
                {option.value === 'all' ? 'Recent' : option.label}
              </button>
            ))}
          </div>
        </div>

        {/* View mode switcher */}
        {viewMode && onViewModeChange && availableViewModes && (
          <ViewSwitcher
            value={viewMode}
            onChange={onViewModeChange}
            options={availableViewModes}
          />
        )}
      </div>

      {/* Search and filters - responsive layout */}
      {(onSearchChange || onTopicsChange) && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {/* Search input */}
          {onSearchChange && (
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
              <Input
                type="search"
                placeholder="Search entries..."
                value={searchQuery || ''}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>
          )}
          {/* Topic filter */}
          {onTopicsChange && (
            <TopicFilter
              selected={selectedTopics || []}
              onChange={onTopicsChange}
            />
          )}
        </div>
      )}

      {/* Filter chips - shown when filters are active */}
      {activeFilters && activeFilters.length > 0 && (
        <FilterChips chips={activeFilters} onClearAll={onClearAllFilters} />
      )}
    </div>
  )
}
